import { inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Status } from '@bcef/ia-shared';
import { InfoClientService } from '@bcef/ia-shared/info-client';
import { patchState, signalStoreFeature, withFeature, withMethods, withState } from '@ngrx/signals';
import { JsonConvert } from 'json2typescript';
import { catchError, map, Observable, of } from 'rxjs';

import { Agency } from '@core/models/agency.model';
import { Agency as AgencyServiceResponse, AgencyService } from '@core/services/agency.service';

import { withLoadingStatus } from './with-loading-status';
import { withStorage } from './with-storage';

export interface AdvisorState {
  agency: Agency | null;
  error: string | null;
}

const advisorInitialState: AdvisorState = {
  agency: null,
  error: null,
};

export function withAgency() {
  return signalStoreFeature(
    withState(advisorInitialState),
    withLoadingStatus(),
    withFeature((_, infoClient = inject(InfoClientService)) => {
      const infoClientData = toSignal(infoClient.getInfoClient())()?.data;
      return withStorage<AgencyServiceResponse>({
        storageType: 'local',
        key: 'contact|agency',
        ttl: 5 * 60 * 1000,
        uid: infoClientData?.client?.ikpiPersonnePhysique,
      });
    }),
    withMethods(
      (store, agencyService = inject(AgencyService), jsonConvert = inject(JsonConvert)) => ({
        getAgency(): Observable<Agency | null> {
          patchState(store, { loadingStatus: Status.PROCESSING, error: null });
          return store
            .loadOrFetch(() => agencyService.getAgency())
            .pipe(
              map((agency) => {
                if (!agency) {
                  return null;
                }
                const sanitizedAgency = jsonConvert.deserialize(agency, Agency);
                patchState(store, {
                  agency: sanitizedAgency,
                  loadingStatus: Status.SUCCESS,
                  error: null,
                });
                return sanitizedAgency;
              }),
              catchError((error) => {
                patchState(store, { loadingStatus: Status.FAIL, error: error });
                return of(null);
              }),
            );
        },
      }),
    ),
  );
}
